<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, defineProps, defineEmits, defineExpose } from "vue";
import { useRouter } from 'vue-router';
import Lottie from "@/components/widgets/lottie.vue";
import Swal from "sweetalert2";
import profileService from "@/services/userProfile-service";

const props = defineProps({
    timeout: {
        type: Number,
        default: 1200000,
        validator: (value) => value >= 10000
    },
    countdownDuration: {
        type: Number,
        default: 300000,
        validator: (value) => value >= 10000
    },
    message: {
        type: String,
        default: "Session verrouillée"
    },
    subMessage: {
        type: String,
        default: "Votre session a été verrouillée par mesure de sécurité"
    },
    showCountdown: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['locked', 'unlocked', 'countdown-tick']);

const router = useRouter();
const isLocked = ref(false);
const showUnlockForm = ref(false);
const countdown = ref(0);
const inactivityTimer = ref(null);
const countdownInterval = ref(null);
const formInactivityTimer = ref(null);
const lastActivity = ref(Date.now());
const navigationHandler = ref(null);
const isInitialized = ref(false);

// Données du formulaire de déverrouillage
const user = ref(JSON.parse(localStorage.getItem("user")) || null);
const password = ref("");
const loading = ref(false);
const errorMessage = ref("");
const showPassword = ref(false);

// Événements à surveiller pour l'activité utilisateur
const activityEvents = [
    'mousemove', 'mousedown', 'click', 'scroll',
    'keypress', 'keydown', 'keyup',
    'touchstart', 'touchmove', 'touchend'
];

// Vérifier si la route courante doit avoir le lockscreen
const shouldHaveLockscreen = computed(() => {
    const currentRoute = router.currentRoute.value;
    return !currentRoute.meta?.noLockscreen;
});

// Condition d'affichage finale
const shouldShowLockScreen = computed(() => {
    return isLocked.value && shouldHaveLockscreen.value;
});

// Formatage du compte à rebours
const formattedCountdown = computed(() => {
    const minutes = Math.floor(countdown.value / 60000);
    const seconds = Math.floor((countdown.value % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

// Génère les initiales de l'utilisateur
const getInitials = (u) => {
    if (!u) return "U";
    const first = (u.firstname || u.username || "").charAt(0).toUpperCase();
    const last = (u.lastname || "").charAt(0).toUpperCase();
    return `${first}${last}` || first;
};

// ─────────────────────────────────────────────
// Gestionnaires d'événements (définis en dehors
// de lockScreen pour pouvoir les retirer proprement)
// ─────────────────────────────────────────────

function preventNavigation(event) {
    if (isLocked.value && shouldHaveLockscreen.value) {
        event.preventDefault();
        event.returnValue = 'Votre session est verrouillée. Déverrouillez-la avant de quitter.';
        return 'Votre session est verrouillée. Déverrouillez-la avant de quitter.';
    }
}

function preventUrlChanges() {
    if (isLocked.value && shouldHaveLockscreen.value) {
        const currentUrl = window.location.href;
        setTimeout(() => {
            if (window.location.href !== currentUrl) {
                window.history.pushState(null, "", currentUrl);
            }
        }, 0);
    }
}

function preventBackgroundInteraction(event) {
    if (isLocked.value && shouldHaveLockscreen.value && !event.target.closest('.lockscreen-modal')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
}

function preventBackgroundKeyboard(event) {
    if (isLocked.value && shouldHaveLockscreen.value && !showUnlockForm.value) {
        const allowedKeys = ['Escape', 'Tab', 'Enter'];
        if (!allowedKeys.includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
}

// ─────────────────────────────────────────────
// Gestion des timers
// ─────────────────────────────────────────────

function clearAllTimers() {
    if (inactivityTimer.value) {
        clearTimeout(inactivityTimer.value);
        inactivityTimer.value = null;
    }
    if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = null;
    }
    if (formInactivityTimer.value) {
        clearTimeout(formInactivityTimer.value);
        formInactivityTimer.value = null;
    }
}

function removeAllLockListeners() {
    window.removeEventListener('beforeunload', preventNavigation);
    window.removeEventListener('popstate', preventUrlChanges);
    window.removeEventListener('hashchange', preventUrlChanges);
    document.removeEventListener('click', preventBackgroundInteraction, true);
    document.removeEventListener('mousedown', preventBackgroundInteraction, true);
    document.removeEventListener('keydown', preventBackgroundKeyboard, true);
    document.removeEventListener('submit', preventBackgroundInteraction, true);
    document.removeEventListener('contextmenu', preventBackgroundInteraction, true);
    if (navigationHandler.value) {
        window.removeEventListener('keydown', navigationHandler.value, true);
        navigationHandler.value = null;
    }
}

function startCountdownInterval() {
    if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = null;
    }
    if (!props.showCountdown) return;

    countdownInterval.value = setInterval(() => {
        if (countdown.value > 0) {
            countdown.value -= 1000;
            emit('countdown-tick', countdown.value);
            if (countdown.value <= 0) {
                forceLogout();
            }
        }
    }, 1000);
}

// ─────────────────────────────────────────────
// Méthode exposée pour mise à jour des settings
// ─────────────────────────────────────────────

function resetWithNewSettings(timeoutMs, countdownMs) {
    console.log('Lockscreen reçoit nouvelles durées:', { timeoutMs, countdownMs });
    clearAllTimers();

    if (isLocked.value) {
        countdown.value = countdownMs;
        startCountdownInterval();
    } else if (shouldHaveLockscreen.value) {
        resetTimer();
    }
}

// ─────────────────────────────────────────────
// Timer d'inactivité principal
// ─────────────────────────────────────────────

function resetTimer() {
    if (!shouldHaveLockscreen.value || isLocked.value) return;

    clearAllTimers();
    lastActivity.value = Date.now();

    inactivityTimer.value = setTimeout(() => {
        lockScreen();
    }, props.timeout);
}

// Timer d'inactivité pour le formulaire (30 secondes sans action → retour écran verrouillé)
function resetFormTimer() {
    clearTimeout(formInactivityTimer.value);
    formInactivityTimer.value = setTimeout(() => {
        backToLockScreen();
    }, 30000);
}

// ─────────────────────────────────────────────
// Actions principales
// ─────────────────────────────────────────────

function lockScreen() {
    if (isLocked.value) return;
    if (!shouldHaveLockscreen.value) return;

    console.log("Verrouillage de l'écran");
    clearAllTimers();

    isLocked.value = true;
    showUnlockForm.value = false;
    countdown.value = props.countdownDuration;

    localStorage.setItem("isLocked", "true");
    localStorage.setItem("lockedTimestamp", Date.now().toString());

    // Bloquer la navigation
    window.addEventListener('beforeunload', preventNavigation);
    window.addEventListener('popstate', preventUrlChanges);
    window.addEventListener('hashchange', preventUrlChanges);

    // Bloquer les interactions avec le fond
    document.addEventListener('click', preventBackgroundInteraction, true);
    document.addEventListener('mousedown', preventBackgroundInteraction, true);
    document.addEventListener('keydown', preventBackgroundKeyboard, true);
    document.addEventListener('submit', preventBackgroundInteraction, true);
    document.addEventListener('contextmenu', preventBackgroundInteraction, true);

    // Bloquer Ctrl+R / F5 uniquement (PAS F12, PAS Ctrl+U)
    navigationHandler.value = (e) => {
        if (
            (e.ctrlKey && e.key === 'r') ||
            (e.key === 'F5') ||
            (e.metaKey && e.key === 'r') ||
            (e.ctrlKey && e.key === 'F5')
        ) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
    };
    window.addEventListener('keydown', navigationHandler.value, true);

    startCountdownInterval();
    emit('locked');
}

function showUnlock() {
    showUnlockForm.value = true;
    if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = null;
    }
    resetFormTimer();
    resetForm();
}

function resetForm() {
    password.value = "";
    errorMessage.value = "";
    loading.value = false;
    showPassword.value = false;
}

function backToLockScreen() {
    showUnlockForm.value = false;
    resetForm();
    clearTimeout(formInactivityTimer.value);

    if (isLocked.value) {
        startCountdownInterval();
    }
}

function unlock() {
    console.log("Déverrouillage de l'écran");
    isLocked.value = false;
    showUnlockForm.value = false;

    clearAllTimers();
    countdown.value = 0;

    removeAllLockListeners();

    localStorage.removeItem("isLocked");
    localStorage.removeItem("lockedTimestamp");

    resetTimer();
    resetForm();
    emit('unlocked');
}

function forceLogout() {
    console.log("Déconnexion forcée");
    clearAllTimers();
    removeAllLockListeners();

    localStorage.removeItem("isLocked");
    localStorage.removeItem("lockedTimestamp");

    router.push("/logout");
}

// ─────────────────────────────────────────────
// Activité utilisateur
// ─────────────────────────────────────────────

function handleActivity() {
    if (!isLocked.value && shouldHaveLockscreen.value) {
        resetTimer();
    }
    if (showUnlockForm.value) {
        resetFormTimer();
    }
}

// ─────────────────────────────────────────────
// Déverrouillage via formulaire
// ─────────────────────────────────────────────

const handleUnlock = async () => {
    if (!password.value.trim()) {
        errorMessage.value = "Veuillez entrer votre mot de passe";
        return;
    }

    loading.value = true;
    errorMessage.value = "";

    try {
        const response = await profileService.login({
            username: user.value.username,
            password: password.value
        });

        if (response.data?.success) {
            const { token, refresh_token, user: userData } = response.data.data;

            localStorage.setItem("token", token);
            localStorage.setItem("refresh_token", refresh_token);

            if (userData) {
                localStorage.setItem("user", JSON.stringify(userData));
                user.value = userData;
            }

            await Swal.fire({
                title: "Déverrouillé !",
                text: "Bon retour !",
                icon: "success",
                confirmButtonColor: "#0ab39c",
                timer: 1500,
                showConfirmButton: false,
                customClass: { container: 'swal-overlay-high-zindex' }
            });

            unlock();
        } else {
            throw new Error(response.data?.message || "Échec du déverrouillage");
        }
    } catch (error) {
        console.error("Erreur déverrouillage:", error);

        let errorMsg = "Mot de passe incorrect ou erreur de connexion";
        if (error.response?.data) {
            errorMsg = error.response.data.message || error.response.data.error_description || errorMsg;
        } else if (error.message) {
            errorMsg = error.message;
        }

        errorMessage.value = errorMsg;

        await Swal.fire({
            title: "Erreur",
            text: errorMsg,
            icon: "error",
            confirmButtonColor: "#f06548",
            timer: 1500,
            showConfirmButton: false,
            customClass: { container: 'swal-overlay-high-zindex' }
        });
    } finally {
        loading.value = false;
    }
};

// Redirection vers la page de login
const login = async () => {
    localStorage.removeItem("isLocked");
    localStorage.removeItem("lockedTimestamp");
    router.push("/login");
};

// ─────────────────────────────────────────────
// Vérification de l'état au réveil de la page
// ─────────────────────────────────────────────

function checkLockStateOnWake() {
    const wasLocked = localStorage.getItem("isLocked") === "true";
    const lockedTimestamp = localStorage.getItem("lockedTimestamp");

    // Rien à faire si l'état est propre
    if (!wasLocked || !lockedTimestamp) return;

    const timeLocked = Date.now() - parseInt(lockedTimestamp);

    if (timeLocked >= props.countdownDuration) {
        // Session expirée pendant l'absence
        console.log("Session expirée pendant le sommeil, déconnexion");
        forceLogout();
        return;
    }

    if (!shouldHaveLockscreen.value) {
        // Page exclue du lockscreen : nettoyer l'état pour éviter tout blocage
        localStorage.removeItem("isLocked");
        localStorage.removeItem("lockedTimestamp");
        return;
    }

    if (!isLocked.value) {
        // Restaurer l'état verrouillé
        isLocked.value = true;
        showUnlockForm.value = false;
        countdown.value = props.countdownDuration - timeLocked;

        if (countdown.value <= 0) {
            forceLogout();
        } else {
            startCountdownInterval();
        }
    }
}

// ─────────────────────────────────────────────
// Watchers
// ─────────────────────────────────────────────

// Surveiller les changements de route
watch(() => router.currentRoute.value, (newRoute) => {
    if (newRoute.meta?.noLockscreen && isLocked.value) {
        // Déverrouillage automatique sur les pages exclues
        unlock();
    }
    if (!newRoute.meta?.noLockscreen && !isLocked.value) {
        resetTimer();
    }
});

// Surveiller les changements de timeout (via props)
watch(() => props.timeout, () => {
    if (!isLocked.value && shouldHaveLockscreen.value) {
        resetTimer();
    }
});

// Surveiller les changements de countdown (via props)
watch(() => props.countdownDuration, (newVal) => {
    if (isLocked.value) {
        countdown.value = newVal;
    }
});

// Garantir que les listeners sont nettoyés si isLocked passe à false
// par n'importe quel chemin (sécurité supplémentaire)
watch(isLocked, (newVal) => {
    if (!newVal) {
        removeAllLockListeners();
    }
});

// ─────────────────────────────────────────────
// Cycle de vie
// ─────────────────────────────────────────────

const visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
        checkLockStateOnWake();
    }
};

const onlineHandler = () => {
    checkLockStateOnWake();
};

onMounted(() => {
    if (isInitialized.value) return;
    isInitialized.value = true;

    // Vérifier l'état au montage (rechargement de page)
    checkLockStateOnWake();

    // Vérifier au retour sur l'onglet
    document.addEventListener('visibilitychange', visibilityHandler);

    // Vérifier à la reconnexion réseau
    window.addEventListener('online', onlineHandler);

    // Écouter l'activité utilisateur
    activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
    });

    // Démarrer le timer d'inactivité si la page le supporte
    if (shouldHaveLockscreen.value && !isLocked.value) {
        resetTimer();
    }

    // NOTE : le bloc qui verrouillait sur F12/Ctrl+U a été supprimé
    // car il causait des verrouillages involontaires lors de l'ouverture
    // des DevTools ou de l'affichage du code source.
});

onBeforeUnmount(() => {
    activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
    });

    clearAllTimers();
    removeAllLockListeners();

    document.removeEventListener('visibilitychange', visibilityHandler);
    window.removeEventListener('online', onlineHandler);
});

// ─────────────────────────────────────────────
// API publique du composant
// ─────────────────────────────────────────────

defineExpose({
    lock: lockScreen,
    unlock,
    forceLogout,
    reset: resetTimer,
    resetWithNewSettings
});
</script>

<template>
    <div v-if="shouldShowLockScreen" class="lockscreen-overlay">
        <!-- Écran principal de verrouillage -->
        <div v-if="!showUnlockForm" class="lockscreen-modal p-4 rounded shadow bg-blur">
            <!-- En-tête -->
            <div class="text-center mb-5">
                <Lottie :options="{
                    loop: true,
                    autoplay: true,
                    animationData: require('@/assets/lockscreen.json'),
                    rendererSettings: {
                        preserveAspectRatio: 'xMidYMid slice'
                    }
                }" :height="100" :width="100" />
                <br>
                <br>
                <h3 class="fs-4 fw-semibold text-white mb-2">{{ message }}</h3>
                <p class="modal-subtitle text-muted">{{ subMessage }}</p>
            </div>

            <!-- Compte à rebours -->
            <div v-if="showCountdown && countdown <= 120000" class="countdown-section">
                <div class="countdown-display">
                    <div class="countdown-circle">
                        <span class="countdown-text">{{ formattedCountdown }}</span>
                    </div>
                </div>
                <div class="countdown-info">
                    <small class="text-muted">
                        Déconnexion automatique dans
                        <strong>{{ formattedCountdown }}</strong>
                    </small>
                </div>
            </div>

            <!-- Actions -->
            <div class="modal-actions">
                <BButton variant="primary" size="md" class="unlock-btn" @click="showUnlock" :disabled="countdown <= 0">
                    <i class="ri-lock-unlock-line me-2"></i>
                    Reprendre la session
                </BButton>

                <BButton v-if="showCountdown && countdown > 0" variant="outline-secondary" size="sm" class="logout-btn"
                    @click="forceLogout">
                    <i class="ri-logout-box-r-line me-2"></i>
                    Se déconnecter
                </BButton>
                <BButton v-if="showCountdown && countdown <= 0" variant="outline-secondary" size="sm" class="logout-btn"
                    @click="forceLogout">
                    <i class="ri-logout-box-r-line me-2"></i>
                    Se reconnecter
                </BButton>
            </div>

            <!-- Note de sécurité -->
            <div v-if="showCountdown && countdown > 0" class="security-note">
                <i class="ri-shield-check-line me-1"></i>
                <small>Pour des raisons de sécurité, votre session sera automatiquement fermée après <strong>{{
                    Math.floor(countdownDuration / 60000)
                        }}</strong> minutes d'inactivité.</small>
            </div>

            <div v-if="showCountdown && countdown <= 0" class="security-note alert-note">
                <i class="ri-alarm-warning-line me-1"></i>
                <small><strong>Session expirée :</strong> Votre session a expiré. Veuillez vous reconnecter pour
                    continuer.</small>
            </div>
        </div>

        <!-- Formulaire de déverrouillage -->
        <div v-else class="lockscreen-modal p-4 rounded shadow bg-blur unlock-form-modal">
            <!-- En-tête -->
            <div class="text-center mb-4">
                <p class="mt-3 fs-15 fw-medium text-white">REPUBLIQUE CENTRAFRICAINE</p>
                <router-link to="#" class="d-inline-block auth-logo">
                    <img src="@/assets/images/mepci.svg" alt="Logo Dûnîa" height="70">
                </router-link>
                <p class="mt-2 fs-13 fw-medium text-white-50">Ministère de l'Économie, du Plan et de la Coopération
                    Internationale (MEPCI).</p>
            </div>

            <!-- Titre -->
            <div class="text-center mt-2 mb-4">
                <h5 class="text-primary fw-semibold">Écran Verrouillé</h5>
                <p class="text-muted mb-0">Entrez votre mot de passe pour déverrouiller</p>
            </div>

            <!-- Avatar + infos utilisateur -->
            <div class="user-thumb text-center mb-4">
                <div class="mb-3">
                    <img v-if="user?.image" :src="user.image" alt="Avatar utilisateur"
                        class="rounded-circle img-thumbnail" style="width: 70px; height: 70px; object-fit: cover;" />
                    <div v-else
                        class="d-flex justify-content-center align-items-center rounded-circle bg-primary text-white fw-bold mx-auto img-thumbnail"
                        style="width: 70px; height: 70px; font-size: 22px;">
                        {{ getInitials(user) }}
                    </div>
                </div>
                <h6 class="mb-1 text-white fw-semibold">{{ user?.firstname?.split(' ')[0] }}</h6>
                <p class="text-muted mb-3">{{ user?.email || user?.username || "Utilisateur" }}</p>
            </div>

            <!-- Formulaire -->
            <div class="p-3">
                <BForm @submit.prevent="handleUnlock" novalidate="true">
                    <div class="mb-4">
                        <label class="form-label text-white fw-medium" for="userpassword">Mot de passe</label>
                        <div class="position-relative">
                            <BFormInput :type="showPassword ? 'text' : 'password'" v-model="password" id="userpassword"
                                placeholder="Entrez votre mot de passe" :disabled="loading" class="pe-5 form-control-lg"
                                @input="resetFormTimer" @focus="resetFormTimer" @keydown="resetFormTimer" />
                            <span class="position-absolute top-50 end-0 translate-middle-y me-3"
                                style="cursor: pointer; z-index: 5;"
                                @click="showPassword = !showPassword; resetFormTimer()">
                                <i class="ri-eye-fill text-muted fs-5" v-if="showPassword"></i>
                                <i class="ri-eye-off-fill text-muted fs-5" v-else></i>
                            </span>
                        </div>
                        <BFormInvalidFeedback v-if="errorMessage" class="d-block mt-2">
                            <i class="ri-error-warning-line me-1"></i>{{ errorMessage }}
                        </BFormInvalidFeedback>
                    </div>

                    <div class="mb-3 mt-4">
                        <BButton variant="primary" class="w-100 py-2 fw-semibold" type="submit"
                            :disabled="loading || !password">
                            <BSpinner small v-if="loading" class="me-2" />
                            <i v-else class="ri-lock-unlock-line me-2"></i>
                            {{ loading ? 'Déverrouillage...' : 'Déverrouiller' }}
                        </BButton>
                    </div>
                </BForm>
            </div>

            <!-- Lien retour et reconnexion -->
            <div class="mt-4 text-center">
                <BButton variant="outline-light" size="sm" class="me-2 mb-2" @click="backToLockScreen">
                    <i class="ri-arrow-left-line me-1"></i>Retour
                </BButton>

                <p class="mb-0 text-muted">
                    Ce n'est pas vous ?
                    <a href="#" @click.prevent="login" class="fw-semibold text-primary text-decoration-underline">
                        <span class="align-middle"> Se reconnecter</span>
                    </a>
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.lockscreen-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
}

.lockscreen-modal {
    border-radius: 20px;
    padding: 2.5rem;
    max-width: 500px;
    width: 95%;
    text-align: center;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    animation: slideUp 0.4s ease-out;
    background: rgba(45, 55, 72, 0.95);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.unlock-form-modal {
    max-width: 520px;
    max-height: 90vh;
    overflow-y: auto;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(40px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Compte à rebours */
.countdown-section {
    margin: 2rem 0;
}

.countdown-display {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
}

.countdown-circle {
    width: 120px;
    height: 120px;
    border: 4px solid #e2e8f0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.countdown-circle::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 4px solid;
    border-color: #405189 transparent transparent transparent;
    border-radius: 50%;
    animation: countdownSpin 1s linear infinite;
}

.countdown-text {
    font-size: 1.5rem;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    color: #405189;
}

@keyframes countdownSpin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.countdown-info {
    margin-top: 1rem;
}

/* Actions */
.modal-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 2.5rem 0 2rem;
}

.unlock-btn {
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.unlock-btn:not(:disabled):hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(64, 81, 137, 0.4);
}

.logout-btn {
    padding: 0.5rem 1rem;
    border-radius: 10px;
    font-weight: 500;
    font-size: 0.9rem;
}

/* Note de sécurité */
.security-note {
    padding-top: 1.5rem;
    border-top: 1px solid #4a5568;
    color: #a0aec0;
}

.security-note i {
    color: #48bb78;
}

/* Alerte de sécurité */
.alert-note {
    background: rgba(245, 101, 101, 0.1);
    border: 1px solid rgba(245, 101, 101, 0.3);
    border-radius: 8px;
    padding: 12px;
    margin-top: 1rem;
}

.alert-note i {
    color: #f06548;
}

.alert-note small {
    color: #f8d7da;
}

/* Formulaire */
.position-relative .form-control {
    padding-right: 3rem;
}

.form-control-lg {
    padding: 0.75rem 1rem;
    font-size: 1rem;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    color: white;
}

.form-control-lg:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: #405189;
    box-shadow: 0 0 0 0.2rem rgba(64, 81, 137, 0.25);
    color: white;
}

.form-control-lg::placeholder {
    color: rgba(255, 255, 255, 0.6);
}

/* Responsive */
@media (max-width: 576px) {
    .lockscreen-modal {
        padding: 2rem 1.5rem;
        margin: 1rem;
        max-width: 95%;
    }

    .unlock-form-modal {
        max-width: 95%;
    }

    .countdown-circle {
        width: 100px;
        height: 100px;
    }

    .countdown-text {
        font-size: 1.3rem;
    }

    .unlock-btn {
        padding: 0.675rem 1.25rem;
        font-size: 0.95rem;
    }
}

@media (max-width: 480px) {
    .lockscreen-modal {
        padding: 1.5rem;
    }

    .countdown-circle {
        width: 90px;
        height: 90px;
    }

    .countdown-text {
        font-size: 1.2rem;
    }

    .form-control-lg {
        padding: 0.625rem 0.875rem;
        font-size: 0.95rem;
    }
}
</style>

<style>
/* Styles globaux SweetAlert — sans scoped */
.swal-overlay-high-zindex {
    z-index: 10000 !important;
}

.swal2-container {
    z-index: 10000 !important;
}

.swal2-popup {
    z-index: 10001 !important;
}
</style>